import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import {
  buildPiMatrixAgentCharacterIndex,
  buildPiMatrixAgentSnapshot,
  buildPiMatrixAgentStructuralRangeIndex,
  type PiMatrixAgentSnapshotBuildSample,
} from '../lib/piMatrixAgentSnapshot'
import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { ChapterCharacterLayer } from '../types/characterLayerTypes'
import type { PiMatrixAgentSnapshot } from '../types/piMatrixAgent'
import type { ChapterStructuralRangeLayer } from '../types/structuralRangeTypes'

const FALLBACK_BUILD_DELAY_MS = 1000
const MAX_IDLE_WAIT_MS = 1000

type IdleWindow = Window & typeof globalThis & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

export interface ScheduledPiMatrixAgentSnapshotSource {
  chapterId: string
  chapterLabel: string
  rows: AnchoredRow[]
  witnesses: Witness[]
  diffEnabled: boolean
  diffPivotWitnessId?: string
  characterIndexEnabled: boolean
  characterLayer: ChapterCharacterLayer
  structuralRangeLayer: ChapterStructuralRangeLayer
}

export interface ScheduledPiMatrixAgentSnapshotAuthority {
  authorityRevisionId: string
  contentHash: string
}

const buildSnapshot = (
  source: ScheduledPiMatrixAgentSnapshotSource,
  buildKind: PiMatrixAgentSnapshotBuildSample['buildKind'],
  authority?: ScheduledPiMatrixAgentSnapshotAuthority,
): PiMatrixAgentSnapshot => {
  const structuralIndex = source.structuralRangeLayer.ranges.some(
    range => range.status === 'confirmed',
  )
    ? buildPiMatrixAgentStructuralRangeIndex(source.structuralRangeLayer, source.rows)
    : null
  return buildPiMatrixAgentSnapshot({
    chapterId: source.chapterId,
    chapterLabel: source.chapterLabel,
    rows: source.rows,
    witnesses: source.witnesses,
    diffEnabled: source.diffEnabled,
    diffPivotWitnessId: source.diffPivotWitnessId,
    indices: [
      ...(source.characterIndexEnabled
        ? [buildPiMatrixAgentCharacterIndex(source.characterLayer)]
        : []),
      ...(structuralIndex ? [structuralIndex] : []),
    ],
    authority,
  }, buildKind)
}

const scheduleSnapshotBuild = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    const handle = setTimeout(callback, FALLBACK_BUILD_DELAY_MS)
    return () => clearTimeout(handle)
  }
  const idleWindow = window as IdleWindow
  if (idleWindow.requestIdleCallback && idleWindow.cancelIdleCallback) {
    const handle = idleWindow.requestIdleCallback(callback, { timeout: MAX_IDLE_WAIT_MS })
    return () => idleWindow.cancelIdleCallback?.(handle)
  }
  const handle = window.setTimeout(callback, FALLBACK_BUILD_DELAY_MS)
  return () => window.clearTimeout(handle)
}

/**
 * Keeps the Pi authority projection current without rebuilding and hashing the
 * whole chapter inside an urgent editor render. Source changes invalidate Pi
 * immediately, coalesce into one idle build, and can be flushed synchronously
 * by an explicit Pi action.
 */
export const useScheduledPiMatrixAgentSnapshot = ({
  source,
  authority,
  onSourceInvalidated,
}: {
  source: ScheduledPiMatrixAgentSnapshotSource
  authority?: ScheduledPiMatrixAgentSnapshotAuthority
  onSourceInvalidated: () => void
}): {
  snapshot: PiMatrixAgentSnapshot
  pending: boolean
  getCurrentSnapshot: () => Promise<PiMatrixAgentSnapshot>
} => {
  const [snapshot, setSnapshot] = useState(() => buildSnapshot(source, 'initial', authority))
  const latestSourceRef = useRef(source)
  const latestAuthorityRef = useRef(authority)
  const builtSourceRef = useRef(source)
  const snapshotRef = useRef(snapshot)
  const invalidationRef = useRef(onSourceInvalidated)
  const generationRef = useRef(0)
  const cancelScheduledBuildRef = useRef<(() => void) | null>(null)

  latestSourceRef.current = source
  latestAuthorityRef.current = authority
  invalidationRef.current = onSourceInvalidated

  const publishSnapshot = useCallback((
    builtSource: ScheduledPiMatrixAgentSnapshotSource,
    nextSnapshot: PiMatrixAgentSnapshot,
  ) => {
    builtSourceRef.current = builtSource
    snapshotRef.current = nextSnapshot
    setSnapshot(nextSnapshot)
  }, [])

  useLayoutEffect(() => {
    if (builtSourceRef.current === source) return undefined

    invalidationRef.current()
    generationRef.current += 1
    const generation = generationRef.current
    cancelScheduledBuildRef.current?.()
    cancelScheduledBuildRef.current = scheduleSnapshotBuild(() => {
      cancelScheduledBuildRef.current = null
      if (generation !== generationRef.current || latestSourceRef.current !== source) return
      publishSnapshot(
        source,
        buildSnapshot(source, 'scheduled', latestAuthorityRef.current),
      )
    })

    return () => {
      cancelScheduledBuildRef.current?.()
      cancelScheduledBuildRef.current = null
    }
  }, [publishSnapshot, source])

  useLayoutEffect(() => {
    if (
      !authority
      || authority.contentHash !== snapshotRef.current.contentHash
      || authority.authorityRevisionId === snapshotRef.current.authorityRevisionId
    ) return
    const authoritySnapshot = {
      ...snapshotRef.current,
      snapshotId: authority.authorityRevisionId,
      authorityRevisionId: authority.authorityRevisionId,
    }
    snapshotRef.current = authoritySnapshot
    setSnapshot(authoritySnapshot)
  }, [authority])

  useLayoutEffect(() => () => {
    generationRef.current += 1
    cancelScheduledBuildRef.current?.()
    cancelScheduledBuildRef.current = null
  }, [])

  const getCurrentSnapshot = useCallback(async (): Promise<PiMatrixAgentSnapshot> => {
    const latestSource = latestSourceRef.current
    if (builtSourceRef.current === latestSource) return snapshotRef.current

    generationRef.current += 1
    cancelScheduledBuildRef.current?.()
    cancelScheduledBuildRef.current = null
    const nextSnapshot = buildSnapshot(latestSource, 'explicit', latestAuthorityRef.current)
    publishSnapshot(latestSource, nextSnapshot)
    return nextSnapshot
  }, [publishSnapshot])

  return {
    snapshot,
    pending: builtSourceRef.current !== source,
    getCurrentSnapshot,
  }
}
